/*
  Note (Hubert, 2023C): File split from ../index.js
*/
import cloneDeep from 'lodash/cloneDeep';
import find from 'lodash/find';
import flatMap from 'lodash/flatMap';
import groupBy from 'lodash/groupBy';
import isEmpty from 'lodash/isEmpty';
import isBoolean from 'lodash/isBoolean';
import keyBy from 'lodash/keyBy';
import map from 'lodash/map';
import mapKeys from 'lodash/mapKeys';
import omit from 'lodash/omit';
import every from 'lodash/every';

import PropTypes from 'prop-types';
import React, {
  useMemo,
  useReducer,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { FilterIcon, MinusIcon, PlusIcon } from '@heroicons/react/outline';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { Random } from '@zedoc/random';

import Filter from './Filter';
import reducer from '../reducer';
import Button from '../../Button';
import { isPrototypeOfTheOtherFilter, buildFixedFirstFilters } from '../utils';

import {
  FILTER_TYPE__ALL_OF,
  FILTER_TYPE__ANY_OF,
} from '../../../common/constants';
import isValidFilter from '../../../common/search/isValidFilter';
import Input from '../../Input';

export const FILTER_CATEGORY__DASHBOARD = 'dashboard';
export const FILTER_CATEGORY__VARIABLE = 'variable';
export const FILTER_CATEGORY__QUESTIONNAIRE = 'questionnaire';
export const FILTER_CATEGORY__OTHER = 'other';
export const FILTER_CATEGORIES = [
  FILTER_CATEGORY__DASHBOARD,
  FILTER_CATEGORY__QUESTIONNAIRE,
  FILTER_CATEGORY__VARIABLE,
  FILTER_CATEGORY__OTHER,
];

const initialFilters = [
  {
    id: Random.id(),
    type: FILTER_TYPE__ALL_OF,
    settings: {
      filters: [
        {
          id: Random.id(),
        },
      ],
    },
    meta: {
      nested: true,
    },
  },
];

const init = ({ value, fixedFirstFilterKey, presets }) => {
  const defaults = isEmpty(value)
    ? buildFixedFirstFilters(presets, fixedFirstFilterKey) || initialFilters
    : value;
  return {
    filtersOrder: map(defaults, 'id'),
    filters: keyBy(defaults, 'id'),
    newValue: null,
  };
};

const Filters = ({
  groupIndex,
  groupId,
  groupType,
  presets,
  value,
  fixedFirstFilterKey,
  allowConditionGroups,
  onChange,
  optionsDisplayCounts,
  optionsSelector,
  optionsSubscription,
  // eslint-disable-next-line no-unused-vars
  onDelete,
  onSubmit,
  onReset,
  extra,
  submitFailed,
  setSubmitFailed,
  isAdvancedView,
  onShowFilters,
  onHideFilters,
  hasSimpleView,
  onFiltersDraftChange,
}) => {
  const { t } = useTranslation();
  const [{ filtersOrder, filters, newValue }, dispatch] = useReducer(
    reducer,
    { value, fixedFirstFilterKey, presets },
    init,
  );

  const isFixedFilter = useCallback(
    (filter, index) =>
      Boolean(
        groupId &&
          groupIndex === 0 &&
          index === 0 &&
          fixedFirstFilterKey &&
          filter.settings?.id === fixedFirstFilterKey,
      ),
    [groupId, groupIndex, fixedFirstFilterKey],
  );

  useEffect(() => {
    if (!onFiltersDraftChange) {
      return;
    }
    onFiltersDraftChange(map(filtersOrder, (id) => filters[id]));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- report live filter tree, not callback identity
  }, [filters, filtersOrder]);

  useEffect(() => {
    if (newValue) {
      dispatch({
        type: 'CHANGE',
        payload: null,
        meta: {
          key: 'newValue',
        },
      });
      onChange(newValue);
    }
  }, [dispatch, newValue, onChange]);

  const handleOnSubmit = useCallback(() => {
    const isValid = every(filters, isValidFilter);
    if (isValid) {
      setSubmitFailed(false);

      dispatch({
        type: 'SUBMIT',
      });

      if (onSubmit) {
        onSubmit();
      }
    } else {
      setSubmitFailed(true);
    }
  }, [onSubmit, filters, setSubmitFailed]);
  const handleFilterOnChange = (id, fields) => {
    dispatch({
      type: 'UPDATE',
      payload: fields,
      meta: {
        id,
        submit: !!groupId,
      },
    });
  };
  const handleOnDelete = useCallback(
    (id) => {
      const filter = filters[id];
      const filterIndex = filtersOrder.indexOf(id);
      if (isFixedFilter(filter, filterIndex)) {
        return;
      }
      if (Object.keys(filters).length === 1) {
        onDelete(groupId);
      } else {
        dispatch({
          type: 'REMOVE',
          meta: {
            id,
            submit: !!groupId,
          },
        });
      }
    },
    [dispatch, filters, filtersOrder, isFixedFilter, onDelete, groupId],
  );
  const handleOnAddCondition = () => {
    const id = Random.id();

    dispatch({
      type: 'INSERT',
      payload: {},
      meta: {
        id,
        submit: !!groupId,
      },
    });
  };
  const handleOnAddConditionGroup = () => {
    dispatch({
      type: 'INSERT',
      payload: {
        type: FILTER_TYPE__ALL_OF,
        settings: {
          filters: [
            {
              id: Random.id(),
            },
          ],
        },
        meta: {
          nested: true,
        },
      },
      meta: {
        id: Random.id(),
      },
    });
  };

  const presetsById = useMemo(
    () => mapKeys(presets, (_, index) => `preset:${index}`),
    [presets],
  );

  const presetsIdsBySettingsId = useMemo(
    () =>
      groupBy(
        Object.keys(presetsById),
        (presetId) => presetsById[presetId]?.settings.id,
      ),
    [presetsById],
  );

  const handleOnSelectSegment = useCallback(
    (id, selectedKey) => {
      const newFilter = cloneDeep(presetsById[selectedKey]);
      dispatch({
        type: 'REPLACE',
        payload: newFilter,
        meta: {
          id,
          submit: !!groupId,
        },
      });
    },
    [dispatch, groupId, presetsById],
  );

  // Carries each preset's index so the option value survives filtering below
  // without having to look the preset back up.
  const segments = useMemo(
    () =>
      map(presets, (preset, index) => ({
        category: preset?.meta?.category || '',
        label: preset?.meta?.display || preset?.name,
        value: `preset:${index}`,
        settingsId: preset?.settings?.id,
      })),
    [presets],
  );

  const groupSegmentsByCategory = useCallback(
    (categorised) =>
      map(groupBy(categorised, 'category'), (options, category) => ({
        label: t(category),
        options: options.map((option) => ({
          label: option.label,
          value: option.value,
        })),
      })),
    [t],
  );

  const segmentOptions = useMemo(
    () => groupSegmentsByCategory(segments),
    [groupSegmentsByCategory, segments],
  );

  const segmentOptionsWithoutFixed = useMemo(
    () =>
      fixedFirstFilterKey
        ? groupSegmentsByCategory(
            segments.filter(
              ({ settingsId }) => settingsId !== fixedFirstFilterKey,
            ),
          )
        : segmentOptions,
    [groupSegmentsByCategory, segments, fixedFirstFilterKey, segmentOptions],
  );

  // Returns one of two memoised arrays rather than building one per row, so
  // rows keep a stable options identity across renders.
  const getSegmentOptions = useCallback(
    (filter, index) =>
      fixedFirstFilterKey && !isFixedFilter(filter, index)
        ? segmentOptionsWithoutFixed
        : segmentOptions,
    [
      fixedFirstFilterKey,
      isFixedFilter,
      segmentOptions,
      segmentOptionsWithoutFixed,
    ],
  );

  const variants = {
    initial: {
      opacity: 0,
      y: '100%',
    },
    animate: {
      opacity: 1,
      y: 0,
    },
    exit: {
      opacity: 0,
      scaleY: 0,
    },
  };

  const [simpleFilterValue, setSimpleFilterValue] = useState();
  const handleOnChangeTest = (event) => {
    const text = event.target.value;

    setSimpleFilterValue(text);

    if (filters) {
      Object.values(filters).forEach((filter) => {
        const nestedFilter = filter.settings.filters[0];
        nestedFilter.state = { text };

        handleFilterOnChange(filter.id, {
          ...filter,
          settings: { filters: [nestedFilter] },
        });
      });
    }
  };

  return (
    <div className="cluster-4 flex-1">
      {isAdvancedView && (
        <div className="stack-2 w-full">
          <AnimatePresence initial={false}>
            {map(filtersOrder, (id, idx) => {
              const filter = filters[id];
              if (filter && filter.meta && filter.meta.nested) {
                return (
                  <motion.div
                    key={id}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    className="cluster-4"
                  >
                    {isAdvancedView && (
                      <span
                        className={`${
                          filtersOrder.length > 1
                            ? 'py-4 w-10 text-center'
                            : 'py-2'
                        }`}
                      >
                        {idx > 0 ? t('or') : t('where')}
                      </span>
                    )}
                    <div
                      className={`${
                        filtersOrder.length > 1
                          ? 'bg-primary-200 dark:bg-neutral-400 p-2 rounded'
                          : ''
                      } flex-1 w-0`}
                    >
                      <Filters
                        groupIndex={idx}
                        groupId={id}
                        groupType={filter.type}
                        presets={presets}
                        fixedFirstFilterKey={fixedFirstFilterKey}
                        allowConditionGroups={allowConditionGroups}
                        value={filter.settings.filters}
                        onChange={(payload) =>
                          dispatch({
                            type: 'CHANGE',
                            payload,
                            meta: {
                              key: `filters.${id}.settings.filters`,
                              submit: !!groupId,
                            },
                          })
                        }
                        onDelete={handleOnDelete}
                        onReset={onReset}
                        optionsDisplayCounts={optionsDisplayCounts}
                        optionsSelector={optionsSelector}
                        optionsSubscription={(params) => {
                          if (!optionsSubscription) {
                            return null;
                          }
                          if (groupType === FILTER_TYPE__ANY_OF) {
                            return optionsSubscription(params);
                          }
                          return optionsSubscription({
                            ...params,
                            currentFilters: map(filtersOrder, (filterId) => {
                              const allowedProps = omit(
                                filters[filterId],
                                'meta',
                              );
                              if (id === filterId) {
                                return {
                                  ...allowedProps,
                                  settings: {
                                    ...filters[filterId]?.settings,
                                    filters: params.currentFilters,
                                  },
                                };
                              }
                              return allowedProps;
                            }),
                          });
                        }}
                        submitFailed={submitFailed}
                        isAdvancedView={isAdvancedView}
                      />
                    </div>
                  </motion.div>
                );
              }
              const segmentValue = find(
                presetsIdsBySettingsId[filter.settings?.id],
                (presetId) =>
                  presetsById[presetId] &&
                  isPrototypeOfTheOtherFilter(presetsById[presetId], filter),
              );
              return (
                <motion.div
                  key={id}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  variants={variants}
                  // NOTE: Had to disable because filters jump around when switching dashboards
                  // Take another look when updating to latest "framer-motion"
                  // positionTransition
                >
                  <Filter
                    id={id}
                    name={filter.name}
                    type={filter.type}
                    condition={filter.condition}
                    segmentOptions={getSegmentOptions(filter, idx)}
                    segmentValue={segmentValue}
                    onSelectSegment={handleOnSelectSegment}
                    state={filter.state}
                    settings={filter.settings}
                    meta={filter.meta}
                    presets={presets}
                    isFixed={isFixedFilter(filter, idx)}
                    onDelete={handleOnDelete}
                    onChange={handleFilterOnChange}
                    onSubmit={handleOnSubmit}
                    onReset={onReset}
                    optionsDisplayCounts={optionsDisplayCounts}
                    optionsSelector={optionsSelector}
                    optionsSubscription={(params) => {
                      if (!optionsSubscription) {
                        return null;
                      }
                      if (groupType === FILTER_TYPE__ANY_OF) {
                        return optionsSubscription(params);
                      }
                      return optionsSubscription({
                        ...params,
                        currentFilters: flatMap(filtersOrder, (filterId) => {
                          if (id === filterId) {
                            return [];
                          }
                          return [omit(filters[filterId], 'meta')];
                        }),
                      });
                    }}
                    submitFailed={submitFailed}
                  />
                </motion.div>
              );
            })}
          </AnimatePresence>

          {isAdvancedView && (
            <>
              {groupId && (
                <div className="cluster-1 justify-between">
                  <Button
                    data-testid="filters-add-condition"
                    type="tertiary"
                    icon={<PlusIcon />}
                    onClick={handleOnAddCondition}
                    className="self-start"
                  >
                    {t('addCondition')}
                  </Button>
                  {groupIndex > 0 && (
                    <Button
                      data-testid="filters-group-delete"
                      type="tertiary"
                      icon={<MinusIcon />}
                      onClick={() => onDelete(groupId)}
                    >
                      {t('removeGroup')}
                    </Button>
                  )}
                </div>
              )}

              {!groupId && (allowConditionGroups || extra) && (
                <div className="flex items-center w-full gap-1">
                  {allowConditionGroups && (
                    <Button
                      data-testid="filters-add-condition-group"
                      type="tertiary"
                      icon={<PlusIcon />}
                      onClick={handleOnAddConditionGroup}
                      className="self-start shrink-0"
                    >
                      {t('addConditionGroup')}
                    </Button>
                  )}
                  {extra}
                </div>
              )}
            </>
          )}
        </div>
      )}
      {!groupId && (
        <>
          {!isAdvancedView && (
            <Input
              data-testid="filters-filter-input"
              value={simpleFilterValue}
              onChange={handleOnChangeTest}
              placeholder="Search by name"
              className="self-center"
            />
          )}
          <div className={`${filtersOrder.length > 1 ? 'py-2' : ''}`}>
            <div className="cluster-2 items-center">
              <Button
                data-testid="filters-filter-submit"
                type="primary"
                onClick={handleOnSubmit}
              >
                {t('search')}
              </Button>
              {!isEmpty(filters) && (
                <Button data-testid="filters-delete-all" onClick={onReset}>
                  {t('clear')}
                </Button>
              )}
              {hasSimpleView && (
                <Button
                  type="ghost"
                  size="medium"
                  icon={<FilterIcon />}
                  onClick={isAdvancedView ? onHideFilters : onShowFilters}
                >
                  {isAdvancedView ? t('hideFilters') : t('showFilters')}
                </Button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

Filters.propTypes = {
  presets: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      name: PropTypes.string,
    }),
  ),
  value: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
  fixedFirstFilterKey: PropTypes.string,
  allowConditionGroups: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  optionsDisplayCounts: PropTypes.bool,
  optionsSubscription: PropTypes.func,
  optionsSelector: PropTypes.shape({
    all: PropTypes.func,
  }),
  extra: PropTypes.node,
  groupIndex: PropTypes.number,
  groupId: PropTypes.string,
  groupType: PropTypes.oneOf([FILTER_TYPE__ALL_OF, FILTER_TYPE__ANY_OF]),
  onDelete: PropTypes.func,
  onReset: PropTypes.func,
  submitFailed: PropTypes.bool,
  isAdvancedView: PropTypes.bool,
  hasSimpleView: PropTypes.bool,
  onShowFilters: PropTypes.func,
  onHideFilters: PropTypes.func,
  setSubmitFailed: PropTypes.func,
  onFiltersDraftChange: PropTypes.func,
};

Filters.defaultProps = {
  presets: [],
  value: null,
  fixedFirstFilterKey: null,
  allowConditionGroups: true,
  onSubmit: () => {},
  optionsDisplayCounts: false,
  optionsSubscription: null,
  optionsSelector: null,
  extra: null,
  groupIndex: null,
  groupId: null,
  groupType: FILTER_TYPE__ANY_OF,
  onDelete: () => {},
  onReset: () => {},
  submitFailed: false,
  isAdvancedView: false,
  hasSimpleView: false,
  onShowFilters: () => {},
  onHideFilters: () => {},
  setSubmitFailed: () => {},
  onFiltersDraftChange: null,
};

export default Filters;
